// @flow

import type { Dispatch } from 'redux';
import {
  REMOVE_RANGE_FILTER,
  REMOVE_TERM_FILTER,
  ADD_TERM_FILTER,
  UPDATE_RANGE_FILTER,
  REMOVE_TERM_LABEL,
  RESET_FILTERS,
  HYDRATE_FILTERS,
  ADD_CUSTOM_FILTER,
  REMOVE_CUSTOM_FILTER,
} from '../../constants';
import { filterAssortment } from '../assortment';
import {
  isSelectedTermFilter,
  isEmptyTermFilter,
  isSelectedCustomFilter,
} from '../../selectors';
import type { FilterState } from '../../types';

export type AddTermFilter = (
  label: string,
  bucket: string
) => {
  type: typeof ADD_TERM_FILTER,
  payload: {
    label: string,
    bucket: string,
  },
};

export const addTermFilter: AddTermFilter = (
  label: string,
  bucket: string,
) => ({
  type: ADD_TERM_FILTER,
  payload: {
    label,
    bucket,
  },
});

export type RemoveTermFilter = (
  label: string,
  bucket: string,
) => {
  type: typeof REMOVE_TERM_FILTER,
  payload: {
    label: string,
    bucket: string,
  },
};

export const removeTermFilter: RemoveTermFilter = (
  label: string,
  bucket: string,
) => ({
  type: REMOVE_TERM_FILTER,
  payload: {
    label,
    bucket,
  },
});

export type RemoveTermLabel = (
  label: string,
) => {
  type: typeof REMOVE_TERM_LABEL,
  payload: {
    label: string,
  },
};

export const removeTermLabel: RemoveTermLabel = (label: string) => ({
  type: REMOVE_TERM_LABEL,
  payload: {
    label,
  },
});

export type RangeFilter = (
  label: string,
  from: number,
  to: number,
) => {
  type: typeof UPDATE_RANGE_FILTER,
  payload: {
    label: string,
    from: number,
    to: number,
  },
};

export type RemoveRangeFilter = (
  label: string,
) => {
  type: typeof REMOVE_RANGE_FILTER,
  payload: {
    label: string,
  },
};

export const removeRangeFilter = (label: string) => ({
  type: REMOVE_RANGE_FILTER,
  payload: {
    label,
  },
});

export const rangeFilter = (label: string, from: number, to: number) => ({
  type: UPDATE_RANGE_FILTER,
  payload: {
    label,
    from,
    to,
  },
});

export type HydrateFilters = (filters: FilterState) => ({
  type: typeof HYDRATE_FILTERS,
  payload: Object,
});

export type HydrateFiltersAction = {
  type: typeof HYDRATE_FILTERS,
  payload: Object,
};

export const hydrateFilters: HydrateFilters = (filters: FilterState) => ({
  type: HYDRATE_FILTERS,
  payload: filters,
});

export type ResetFilters = () => ({
  type: typeof RESET_FILTERS,
});

export type ResetFiltersAction = {
  type: typeof RESET_FILTERS,
};

export const resetFilters: ResetFilters = () => ({
  type: RESET_FILTERS,
});

export const deleteRangeFilter = (label: string) => (dispatch: Dispatch<any>) => {
  dispatch(removeRangeFilter(label));
  dispatch(filterAssortment());
};

export const removeAllFilters = () => (dispatch: Dispatch<any>) => {
  dispatch(resetFilters());
  dispatch(filterAssortment());
};

export const applyRangeFilter = (label: string, from: number, to: number) => (
  dispatch: Dispatch<any>,
) => {
  dispatch(rangeFilter(label, from, to));
  dispatch(filterAssortment());
};

export type AddCustomFilter = (key: string) => ({
  type: typeof ADD_CUSTOM_FILTER,
  payload: {
    key: string,
  },
});

export type AddCustomFilterAction = {
  type: typeof ADD_CUSTOM_FILTER,
  payload: {
    key: string,
  },
};

export const addCustomFilter: AddCustomFilter = key => ({
  type: ADD_CUSTOM_FILTER,
  payload: {
    key,
  },
});

export type RemoveCustomFilter = (key: string) => ({
  type: typeof REMOVE_CUSTOM_FILTER,
  payload: {
    key: string,
  },
});

export type RemoveCustomFilterAction = {
  type: typeof REMOVE_CUSTOM_FILTER,
  payload: {
    key: string,
  },
};

export const removeCustomFilter: RemoveCustomFilter = key => ({
  type: REMOVE_CUSTOM_FILTER,
  payload: {
    key,
  },
});

export const toggleCustomFilter = (key: string) => (
  dispatch: Dispatch<any>,
  getState: Function,
) => {
  const state = getState();
  if (isSelectedCustomFilter(state, key)) {
    dispatch(removeCustomFilter(key));
    dispatch(filterAssortment());
    return;
  }
  dispatch(addCustomFilter(key));
  dispatch(filterAssortment());
};

export const toggleTermFilter = (label: string, bucket: string) => (
  dispatch: Dispatch<any>,
  getState: Function,
) => {
  const state = getState();

  if (isSelectedTermFilter(state, label, bucket)) {
    dispatch(removeTermFilter(label, bucket));

    // check if this was the last filter
    // if so remove the whole label
    // we need to apply this on the new state
    if (!isEmptyTermFilter(getState(), label)) {
      dispatch(removeTermLabel(label));
    }

    dispatch(filterAssortment());
    return;
  }

  dispatch(addTermFilter(label, bucket));
  dispatch(filterAssortment(label));
};

export default null;
